/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/hues.ts
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-HUES-001
 * PURPOSE: One hue vocabulary for decision accents and summary segment dots.
 * ROLE IN ARCHITECTURE: Pure presentation tokens under the triage band leaves.
 * MAIN EXPORTS: decisionHue, SUMMARY_SEGMENT_DOTS.
 * NON-RESPONSIBILITIES: No layout, no state — class strings only.
 * NOTES FOR FUTURE AI: Plan "Premium visual language": ONE accent hue dominates
 *   at a time, chosen by the pending decision; hues reinforce, never replace text.
 */
import type { ReaderDecisionKind } from '../model/types'

export interface DecisionHue {
  /** 2px left accent on the decision line (border-l-2 + this). */
  accent: string
  /** Small status dot. */
  dot: string
  /** Filled 24px confirm circle treatment. */
  fill: string
}

const HUES: Readonly<Record<ReaderDecisionKind, DecisionHue>> = {
  organisation: {
    accent: 'border-l-rose-500',
    dot: 'bg-rose-500',
    fill: 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700',
  },
  bill: {
    accent: 'border-l-emerald-500',
    dot: 'bg-emerald-500',
    fill: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
  },
  estimator: {
    accent: 'border-l-sky-500',
    dot: 'bg-sky-500',
    fill: 'border-sky-600 bg-sky-600 text-white hover:bg-sky-700',
  },
  placement: {
    accent: 'border-l-indigo-500',
    dot: 'bg-indigo-500',
    fill: 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700',
  },
  suggested_org: {
    accent: 'border-l-violet-500',
    dot: 'bg-violet-500',
    fill: 'border-violet-600 bg-violet-600 text-white hover:bg-violet-700',
  },
  suggested_job: {
    accent: 'border-l-blue-500',
    dot: 'bg-blue-500',
    fill: 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700',
  },
  classification: {
    accent: 'border-l-amber-500',
    dot: 'bg-amber-500',
    fill: 'border-amber-600 bg-amber-600 text-white hover:bg-amber-700',
  },
}

/** Resolve the single dominant hue for one decision kind. Pure I → O. */
export function decisionHue(kind: ReaderDecisionKind): DecisionHue {
  return HUES[kind]
}

/** Resolved-summary segment dots: company / classification / placements / signals. */
export const SUMMARY_SEGMENT_DOTS = {
  company: 'bg-sky-500',
  classification: 'bg-violet-500',
  placements: 'bg-indigo-500',
  signals: 'bg-amber-500',
} as const
